/**
 * Product List Content Component
 * Reusable component for displaying and selecting products
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Tabs, Spin, Empty, Avatar } from "antd";
import { SearchOutlined, RightOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from "../pages/ProductList.module.css";
import { getProductList, getFnaProductList, getProductCompanyList, getProductCategoryList, getSelectMetadataByCode, getMetadataLabel } from "@ifa/utils";
import type { ProductCompanyListVO, ProductListVO } from "@ifa/utils";
import hotIcon from "../assets/img/hot.png";

// Hook: useDebounce
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

export interface ProductListContentProps {
  selectMode?: boolean;
  onSelect?: (product: ProductListVO) => void;
  title?: string;
  hideHeader?: boolean;
  productType?: string;
  companyCode?: string; // 保险公司代码过滤（用于附加险）
  orderFnaDTO?: any; // FNA数据传递给产品列表接口
  isFna?: boolean; // 是否使用 FNA 产品列表接口
  showFnaTip?: boolean; // 是否显示 FNA 提示信息
}

export function ProductListContent({ selectMode, onSelect, title, hideHeader, productType, companyCode, orderFnaDTO, isFna, showFnaTip }: ProductListContentProps) {
  const navigate = useNavigate();
  const orderFnaDTOKey = useMemo(
    () => (isFna && orderFnaDTO ? JSON.stringify(orderFnaDTO) : ""),
    [isFna, orderFnaDTO],
  );

  // States
  const [companies, setCompanies] = useState<ProductCompanyListVO[]>([]);
  const [products, setProducts] = useState<ProductListVO[]>([]);
  const [categories, setCategories] = useState<{ key: string; label: string }[]>([{ key: "all", label: "全部" }]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);

  // Filters
  // 如果传入了 companyCode，则默认选中该公司，否则选择"全部"
  const [selectedCompany, setSelectedCompany] = useState(companyCode || "all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(12);
  const [totalPages, setTotalPages] = useState(0);

  // Pull to refresh & Infinite Scroll refs
  const [pullY, setPullY] = useState(0);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const orderFnaDTORef = useRef(orderFnaDTO);
  const refreshRequestKeyRef = useRef<string>("");

  useEffect(() => {
    orderFnaDTORef.current = orderFnaDTO;
  }, [orderFnaDTO]);

  // Company Map for quick lookup
  const companyMap = useMemo(() => {
    return companies.reduce(
      (acc, company) => {
        if (company.code) {
          acc[company.code] = company;
        }
        return acc;
      },
      {} as Record<string, ProductCompanyListVO>,
    );
  }, [companies]);

  // Fetch Companies
  useEffect(() => {
    const fetchCompanies = async () => {
      setCompanyLoading(true);
      try {
        const res = await getProductCompanyList();
        setCompanies(res || []);
      } catch (error) {
        console.error("Failed to fetch companies:", error);
      } finally {
        setCompanyLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getProductCategoryList();
        if (res && Array.isArray(res)) {
          const newCategories = res.map((item: any) => ({
            key: item.code || item.categoryCode || item.key,
            label: item.name || item.label,
          }));
          setCategories([{ key: "all", label: "全部" }, ...newCategories]);
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch Products
  const fetchProducts = useCallback(
    async (isRefresh: boolean, page: number) => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const params: any = {
          pageNum: page,
          pageSize: pageSize,
          keyword: debouncedSearchText,
        };

        if (selectedCompany !== "all") {
          params.companyCode = selectedCompany;
        }

        if (selectedCategory !== "all") {
          params.categoryCode = selectedCategory;
        }

        if (productType) {
          params.type = productType;
        }

        if (isFna && orderFnaDTORef.current) {
          params.orderFnaDTO = orderFnaDTORef.current;
        }

        // Pass signal to API if supported, otherwise just ignore result if aborted
        const res = isFna ? await getFnaProductList(params) : await getProductList(params);

        // If aborted, don't update state (simplified check)
        if (abortControllerRef.current !== controller) return;

        if (res) {
          const list = res.content || [];
          if (isRefresh) {
            setProducts(list);
            setCurrentPage(1);
          } else {
            setProducts((prev) => [...prev, ...list]);
            setCurrentPage(page);
          }
          setTotalPages(res.page.totalPages || 0);
        }
      } catch (error: any) {
        if (error.name === "AbortError") return;
        console.error("Failed to fetch products:", error);
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          if (isRefresh) {
            setRefreshing(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [pageSize, selectedCompany, selectedCategory, debouncedSearchText, productType, isFna],
  );

  // Trigger refresh when filters change
  useEffect(() => {
    const refreshRequestKey = JSON.stringify({
      selectedCompany,
      selectedCategory,
      debouncedSearchText,
      productType: productType || "",
      orderFnaDTOKey,
      isFna: !!isFna,
    });

    if (refreshRequestKeyRef.current === refreshRequestKey) {
      return;
    }

    refreshRequestKeyRef.current = refreshRequestKey;
    fetchProducts(true, 1);
  }, [
    fetchProducts,
    selectedCompany,
    selectedCategory,
    debouncedSearchText,
    productType,
    orderFnaDTOKey,
    isFna,
  ]);

  // Handle Search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Handle Company Change
  const handleCompanyChange = (companyCode: string) => {
    setSelectedCompany(companyCode);
  };

  // Handle Category Change
  const handleCategoryChange = (key: string) => {
    setSelectedCategory(key);
  };

  const getCategoryLabel = (code: string) => {
    const cat = categories.find((c) => c.key === code);
    return cat ? cat.label : code;
  };

  const getCompanyName = (code: string | undefined) => {
    if (!code) return "";
    return companyMap[code]?.name || code;
  };

  const getCategoryStyle = (code: string | undefined) => {
    if (!code) return "tagDefault";

    // 转为大写进行匹配
    const upperCode = code.toUpperCase();
    const styleMap: Record<string, string> = {
      MEDICAL: "tagMedical",
      LIFE: "tagLife",
      TERM: "tagLife",
      UNIVERSAL: "tagLife",
      CRITICAL_ILLNESS: "tagCriticalIllness",
      CRITICAL: "tagCriticalIllness",
      SAVINGS: "tagSavings",
      ANNUITY: "tagSavings",
    };
    return styleMap[upperCode] || "tagDefault";
  };
  const getLicensePlateLabel = (code: string | undefined) => {
    if (!code) return "-";
    const metadata = getSelectMetadataByCode("LICENSE_PLATE");
    const item = metadata?.items?.find((i) => i.key === code);
    return item?.name || code;
  };

  const formatTermText = useCallback((terms: string[] | string | undefined, metadataCode: string) => {
    const normalizedTerms = Array.isArray(terms) ? terms : terms ? [terms] : [];
    if (normalizedTerms.length === 0) return "-";
    return (
      normalizedTerms
        .map((term) => getMetadataLabel(metadataCode, term, undefined, term || "-"))
        .filter((term) => !!term && term !== "-")
        .join(" / ") || "-"
    );
  }, []);

  // Handle Infinite Scroll
  const handleLoadMore = useCallback(() => {
    if (loading || refreshing || currentPage >= totalPages) return;
    fetchProducts(false, currentPage + 1);
  }, [loading, refreshing, currentPage, totalPages, fetchProducts]);

  // Pull to refresh handlers
  const onTouchStart = (e: React.TouchEvent) => {
    if (listRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isPulling.current) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    if (diff > 0 && listRef.current?.scrollTop === 0) {
      // Dampen the pull
      setPullY(Math.min(diff * 0.4, 100));
    } else {
      isPulling.current = false;
      setPullY(0);
    }
  };

  const onTouchEnd = async () => {
    isPulling.current = false;
    if (pullY > 60) {
      // Trigger refresh
      fetchProducts(true, 1);
    }
    setPullY(0);
  };

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loading && !refreshing && currentPage < totalPages) {
          handleLoadMore();
        }
      },
      {
        root: listRef.current,
        threshold: 0.1,
        rootMargin: "100px",
      },
    );
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    return () => observer.disconnect();
  }, [loading, refreshing, currentPage, totalPages, handleLoadMore]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* FNA 产品提示信息 - 移到 productLayout 外面 */}
      {showFnaTip && (
        <div style={{ padding: "0 26px 6px 0", backgroundColor: "#fff", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#fff1f0",
              border: "1px solid #ffa39e",
              borderRadius: "8px",
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: "2px" }}>
              <path d="M9 1.5L15.75 13.5H2.25L9 1.5Z" stroke="#ff4d4f" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              <path d="M9 6.5V9.5M9 11.5H9.01" stroke="#ff4d4f" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span style={{ fontSize: "13px", color: "#cf1322", lineHeight: "1.6" }}>
              某款產品提示"不可介紹和銷售"，原因是該產品不符合 FNA
              問題的答覆（包括：購買保險產品目標不符、保障年期不符、供款年期不符），或是您的掛牌牌照不獲准銷售此產品，導致該產品被系統過濾。請您調整 FNA 問題的答覆後再嘗試。
            </span>
          </div>
        </div>
      )}
      <div className={styles.productLayout} style={{ flex: 1, minHeight: 0 }}>
        <div className={styles.productContentBody}>
          {/* 左侧保险公司筛选 (Sidebar) - 仅在未指定 companyCode 时显示 */}
          {!companyCode && (
            <div className={styles.productSidebar}>
              <div className={styles.sidebarTitle}>保險公司</div>
              <aside className={styles.productAside}>
                <div className={styles.companyList}>
                  {/* "全部" Option */}
                  <div className={`${styles.companyItem} ${selectedCompany === "all" ? styles.active : ""}`} onClick={() => handleCompanyChange("all")}>
                    <div className={styles.companyItemContent}>
                      <span className={styles.companyAllTag}>ALL</span>
                      <span className={styles.companyName}>全部</span>
                    </div>
                  </div>

                  {/* Company List */}
                  {companyLoading ? (
                    <div style={{ textAlign: "center", padding: 20 }}>
                      <Spin size="small" />
                    </div>
                  ) : (
                    companies.map((company) => (
                      <div key={company.code} className={`${styles.companyItem} ${selectedCompany === company.code ? styles.active : ""}`} onClick={() => handleCompanyChange(company.code || "")}>
                        <div className={styles.companyItemContent}>
                          {company.logo ? (
                            <Avatar src={company.logo} size="small" shape="square" className={styles.companyLogo} />
                          ) : (
                            <Avatar size="small" shape="square" className={styles.companyLogo}>
                              {company.name?.substring(0, 1)}
                            </Avatar>
                          )}
                          <span className={styles.companyName}>{company.name}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </aside>
            </div>
          )}

          {/* 右侧主内容区 (Main) */}
          <main className={styles.productMain}>
            {/* Top Header Row */}
            {!hideHeader && (
              <header className={styles.productPageHeader}>
                <div className={styles.productTitle}>{title || "產品列表"}</div>
                <Input placeholder="輸入關鍵詞搜尋產品" prefix={<SearchOutlined />} className={styles.productSearchInput} value={searchText} onChange={handleSearch} allowClear />
              </header>
            )}
            {/* Categories Tabs */}
            <div className={styles.productCategoriesWrapper}>
              <Tabs
                activeKey={selectedCategory}
                onChange={handleCategoryChange}
                items={categories.map((cat) => ({
                  key: cat.key,
                  label: <span className={`${styles.categoryTab} ${selectedCategory === cat.key ? styles.active : ""}`}>{cat.label}</span>,
                }))}
                tabBarStyle={{ borderBottom: "none", marginBottom: 0 }}
              />
            </div>

            {/* Product List Scrollable Area */}
            <div className={styles.productListWrapper}>
              {/* Pull Down Indicator */}
              <div
                className={styles.pullDownIndicator}
                style={{
                  height: pullY,
                  opacity: pullY > 0 ? 1 : 0,
                  transition: isPulling.current ? "none" : "height 0.3s, opacity 0.3s",
                }}
              >
                <div className={styles.pullContent}>
                  {pullY > 60 ? <LoadingOutlined style={{ fontSize: 20 }} /> : <ReloadOutlined rotate={pullY * 2} style={{ fontSize: 20 }} />}
                  <span style={{ marginLeft: 8 }}>{pullY > 60 ? "釋放刷新" : "下拉刷新"}</span>
                </div>
              </div>

              {loading && products.length === 0 && (
                <div className={styles.productLoadingOverlay}>
                  <Spin size="large" />
                </div>
              )}

              <div
                className={styles.productList}
                ref={listRef}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={{
                  transform: `translateY(${pullY}px)`,
                  transition: isPulling.current ? "none" : "transform 0.3s",
                }}
              >
                {products.length > 0 ? (
                  <>
                    <div className={styles.productGrid}>
                      {products.map((product) => {
                        // 检查产品是否不可选择（FNA 产品列表中 isSelected 为 false）
                        const isUnavailable = isFna && product.isSelected === false;
                        const paymentTermText = formatTermText(product.paymentTerm, "CONTRIBUTION_YEARS");
                        const policyPeriodText = formatTermText(product.policyPeriods, "PERIOD");

                        return (
                          <div
                            key={product.productCode}
                            className={styles.productCard}
                            onClick={() => {
                              // 如果产品不可用，阻止选择
                              if (isUnavailable) {
                                return;
                              }
                              if (selectMode && onSelect) {
                                onSelect(product);
                              } else {
                                navigate(`/products/${product.productCode}/info`);
                              }
                            }}
                            style={{
                              opacity: isUnavailable ? 0.6 : 1,
                              cursor: isUnavailable ? "not-allowed" : "pointer",
                            }}
                          >
                            <div className={styles.productCardContent}>
                              {/* Top: Name */}
                              <div className={styles.productInfoSection}>
                                <div className={styles.productName}>
                                  {product.hot ? (
                                    <div className={styles.hotIcon}>
                                      <img src={hotIcon} alt="Hot" />
                                    </div>
                                  ) : (
                                    ""
                                  )}
                                  <span className={styles.productNameText}>{product.productName}</span>
                                </div>
                              </div>

                              {/* Middle: Tags */}
                              <div className={styles.productTagsRow}>
                                {/* Category Tag */}
                                <span className={`${styles.productTag} ${styles[getCategoryStyle(product.categoryCode)]}`}>{getCategoryLabel(product.categoryName || "")}</span>
                                {/* Company Tag */}
                                <span className={`${styles.productTag} ${styles.tagCompany}`}>{getCompanyName(product.companyCode)}</span>

                                {/* License Plate Tag */}
                                {product.licensePlate &&
                                  product.licensePlate.length > 0 &&
                                  product.licensePlate.map((lp) => (
                                    <span key={lp} className={`${styles.productTag} ${styles.tagLicensePlate}`}>
                                      {getLicensePlateLabel(lp) || "-"}
                                    </span>
                                  ))}
                              </div>

                              <div className={styles.productTermInfo}>
                                <div className={styles.productTermRow}>
                                  <span className={styles.productTermLabel}>繳費年期：</span>
                                  <span className={styles.productTermValue}>{paymentTermText}</span>
                                </div>
                                <div className={styles.productTermRow}>
                                  <span className={styles.productTermLabel}>保障年期：</span>
                                  <span className={styles.productTermValue}>{policyPeriodText}</span>
                                </div>
                              </div>

                              <div style={{ flex: 1 }}></div>

                              {/* Bottom: Action or Unavailable Badge */}
                              {isUnavailable ? (
                                <div className={styles.productCardFooter}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{
                                        padding: "4px 12px",
                                        backgroundColor: "#f5f5f5",
                                        color: "#999",
                                        fontSize: "13px",
                                        borderRadius: "4px",
                                        fontWeight: 500,
                                      }}
                                    >
                                      不可介紹和銷售
                                    </span>
                                  </div>
                                </div>
                              ) : !selectMode ? (
                                <div className={styles.productCardFooter}>
                                  <div className={styles.viewDetailLink}>
                                    查看產品資料 <RightOutlined style={{ fontSize: 12 }} />
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Loading Indicator for Infinite Scroll */}
                    <div
                      ref={loadMoreRef}
                      style={{
                        textAlign: "center",
                        padding: "16px 0",
                        height: 50,
                        opacity: loading && products.length > 0 ? 1 : 0,
                      }}
                    >
                      <Spin tip="載入中..." />
                    </div>
                  </>
                ) : (
                  !loading && (
                    <div className={styles.productEmpty}>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暫無符合條件的產品" />
                    </div>
                  )
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
